/**
 * HTS chapters the Tariff Checker refuses to run.
 *
 * Chapters 98 and 99 hold national special provisions rather than product
 * classifications. In the HTSUS, chapter 98 is the special classification
 * provisions (U.S. goods returned, goods assembled abroad, …) and chapter 99
 * is the additional duties (Sections 301/232, IEEPA, quotas) that the checker
 * itself layers on top of a chapter 1–97 code. The checker's pipeline starts
 * from that chapter 1–97 code and doesn't handle a chapter 98/99 one.
 *
 * Chapter 99 was only ever kept out indirectly: its lines are 8-digit, so they
 * never reached the 10-digit-only suggestion list and failed the 10-digit
 * submit check. Chapter 98 has 10-digit statistical lines and got through
 * both, hence this explicit check, shared by the HTS input and every path
 * that starts a lookup.
 */
export const UNSUPPORTED_HTS_CHAPTERS = ['98', '99'] as const;

export type UnsupportedHtsChapter = (typeof UNSUPPORTED_HTS_CHAPTERS)[number];

/**
 * The unsupported chapter `code` falls in, or null. Accepts dotted or raw
 * digits, and stays null until two digits are typed so chapters 90–97 don't
 * flash a warning on the first keystroke.
 */
export const getUnsupportedHtsChapter = (
  code: string | null | undefined,
): UnsupportedHtsChapter | null => {
  const chapter = String(code ?? '')
    .replace(/\D/g, '')
    .slice(0, 2);
  return (UNSUPPORTED_HTS_CHAPTERS as readonly string[]).includes(chapter)
    ? (chapter as UnsupportedHtsChapter)
    : null;
};

export const unsupportedHtsChapterMessage = (
  chapter: UnsupportedHtsChapter,
): string =>
  `Chapter ${chapter} codes aren't supported. Enter the product's HTS code from chapters 1–97 instead.`;
